import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import Button from '../ui/Button';
import { Notice } from '../labour/shared';
import harvestService from '../../services/harvest';
import { errorMessage } from '../../utils/apiResponse';
import type { ActualWindowRow, ActualWindows } from '../../types/harvest';

const STATUS: Record<string, string> = { estimate: 'Estimate', confirmed: 'Confirmed', actual: 'Actual' };

function Outcome({ row, canPlan, onApply }: { row: ActualWindowRow; canPlan: boolean; onApply: () => void }) {
  if (row.action === 'same') return <span className="text-green-700">Up to date</span>;
  if (row.action === 'differs') {
    return (
      <span className="flex flex-wrap items-center gap-2 justify-end">
        <span className="text-amber-700">Differs from the {row.existing?.status === 'confirmed' ? 'confirmed' : 'typed-in'} window</span>
        {canPlan && <Button size="sm" variant="outline" onClick={onApply}>Use bins’ dates</Button>}
      </span>
    );
  }
  if (row.applied) return <span className="text-green-700">Written</span>;
  return <span className="text-neutral-600">{row.action === 'create' ? 'Will be written' : 'Will replace the estimate'} tonight</span>;
}

/**
 * First and last bin picked per cultivar area against the harvest window on
 * file. Bins write windows nobody confirmed; a confirmed or typed-in window is
 * only changed when someone presses "Use bins' dates". Windows gate spray
 * withholding checks, so this is shown rather than done silently.
 */
export default function HarvestDatesPanel({ season, farmName, canPlan }: { season: number; farmName: string | null; canPlan: boolean }) {
  const [data, setData] = useState<ActualWindows | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await harvestService.getActualWindows());
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load harvest dates'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(errorMessage(e, 'That did not work'));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="text-sm text-neutral-500 py-6 text-center">{error ?? 'Loading…'}</div>;
  const rows = data.rows.filter(r => r.bin_season === season && (!farmName || r.farm_name === farmName));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-neutral-500 max-w-2xl">
          The first and last bin picked in each cultivar area (two bins or more). These become the area’s actual harvest
          window, which spray withholding checks use. While picking goes on, the window keeps its planned end.
        </p>
        {canPlan && data.record_actual_windows && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => harvestService.syncActualWindows())}>
            <RefreshCw className="h-4 w-4" /> Update windows now
          </Button>
        )}
      </div>
      {!data.record_actual_windows && <Notice tone="warn">Recording actual windows from bins is switched off in Shed → Settings. Nothing here is written.</Notice>}
      {error && <Notice tone="error">{error}</Notice>}
      {rows.length === 0 ? <p className="text-sm text-neutral-500 py-6 text-center">No bins picked in this season yet.</p> : (
        <div className="overflow-x-auto border border-neutral-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Area</th>
                <th className="text-right px-3 py-2 font-medium">Bins</th>
                <th className="text-left px-3 py-2 font-medium whitespace-nowrap">Picked</th>
                <th className="text-left px-3 py-2 font-medium whitespace-nowrap">Window on file</th>
                <th className="text-right px-3 py-2 font-medium">&nbsp;</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {rows.map(r => (
                <tr key={`${r.cultivar_area_id}:${r.bin_season}`}>
                  <td className="px-3 py-2 min-w-[11rem]">
                    <p className="text-neutral-900">{r.cultivar_area_name}</p>
                    <p className="text-xs text-neutral-500">{[r.farm_name, r.block_name, r.cultivar_name].filter(Boolean).join(' · ')}</p>
                  </td>
                  <td className="px-3 py-2 text-right">{r.bins}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.first_pick} → {r.last_pick}
                    {r.ongoing && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">picking</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.existing ? (
                      <>
                        {r.existing.earliest_harvest_date} → {r.existing.expected_end_date ?? '?'}
                        <span className="block text-xs text-neutral-500">
                          {STATUS[r.existing.status]}{r.existing.source === 'bins' ? ' · from bins' : ''}
                        </span>
                      </>
                    ) : <span className="text-neutral-400">none</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Outcome row={r} canPlan={canPlan}
                      onApply={() => run(() => harvestService.applyActualWindow(r.cultivar_area_id, r.window_year))} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
